/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Pagination';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { buttonSize } from '../../registry/constants';

const PaginationManager: ComponentManagerFn<'Pagination'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Pagination'>(platform, 'Pagination'),
    type: 'Pagination',
    name: 'Pagination',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['DataStore', 'ReadOnlyDataStore'],
      },
      withUserInput: {
        label: 'Need input field',
        type: 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: (color: string) => {
          return ['primary', 'secondary'].includes(color);
        },
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: toOptions(['outlined', 'text']),
      },
      shape: {
        label: 'Shape',
        type: 'select',
        options: toOptions(['circular', 'rounded']),
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'color':
        case 'variant':
        case 'shape':
        case 'size':
          return !props.withUserInput;
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.alias,
    category: 'Forms',
    icon: { iconName: 'chevron-square-right' },
    description: `**Overview**<br/>
The Pagination component is a navigation control used to manage large datasets. Instead of loading all records at once, it allows users to browse through data in smaller, manageable chunks (pages). It connects directly to a Data Store and visualizes the current page number along with controls to move forward, backward, or jump to specific sections.
Use this component when you have a list, grid, or custom display of records that exceeds a single screen's capacity and requires efficient navigation.

**1. Data Connection**<br/>
To function, the pagination control must know which dataset it is controlling.
* **Alias:** Selects the Data Store (or **ReadOnlyDataStore**) to link with.
* **Mechanism:** Once linked, clicking "Next" on this component automatically triggers the Data Store to fetch the next set of records from the server or local cache.

**2. Navigation Style**<br/>
You can configure how users interact with the page numbers.
* **Need Input Field (withUserInput):** A boolean toggle.
    * **Enabled:** Replaces the standard button row with a text input box. This allows users to type a specific page number (e.g., "42") and press Enter to jump directly there.
    * **Disabled:** Displays standard "Previous", "Next", and numbered buttons (e.g., < 1 2 3 ... 10 >).

**3. Visual Customization**<br/>
(Note: These styling options are only visible when **Need Input Field** is disabled.)
* **Color:** Sets the active color of the selected page button. Options: **primary** or **secondary**.
* **Variant:** Defines the border style. Options: **outlined** (border only) or **text** (minimalist, no border).
* **Shape:** Defines the geometry of the buttons. Options: **circular** (round) or **rounded** (square with soft corners).
* **Size:** Controls the physical size of the buttons (Small, Medium, Large).

**4. Interactivity**<br/>
* **TriggerOnClick:** You can attach a **TriggerOnClick** child component. This is useful if you need to execute additional logic—such as scrolling to the top of the page or logging analytics—whenever a user changes the page.

**5. Best Use Cases**<br/>
* **Search Results:** Displaying 10 results per page at the bottom of a search query.
* **Custom Lists:** Adding navigation to a custom-built "Card View" layout where a standard Data Grid is not used.
* **Mobile Interfaces:** Using the **Need Input Field** mode to save screen space on mobile devices where a long row of buttons would not fit.`,
  };
};

export default PaginationManager;
